import React, { useEffect, useState, useCallback } from 'react';
import { enterpriseAdminService, enterpriseService, wordService, adminService } from '../services';
import { useAuth } from '../context/AuthContext';
import { Enterprise } from '../models';
import './EnterpriseAdmin.css';
import SkeletonManager from '../components/loaders/SkeletonManager';
import { useFullScreenTextarea } from '../hooks/useFullScreenTextEditor';
import { isCapacitor } from '../utils/platform';
import { SPECIALTIES } from '../constants/specialties';
import FullScreenTextEditor from '../components/editors/FullScreenTextEditor';
import useNotification from '../components/Notifications/useNotifications';
import BackButton from '../components/buttons/BackButton';
import { 
  GeneralTab, 
  UsersTab, 
  ReportTypesTab, 
  TemplatesTab, 
  AccessLogsTab 
} from './tabs';
import { createLogger } from '../services/Debug';

// Utility function to truncate text
const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

function EnterpriseAdmin() {
  // Loading state
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { profile } = useAuth();
  const log = createLogger('EnterpriseAdmin');
  // Enterprise and tab state
  const [enterprises, setEnterprises] = useState([]);
  const [enterpriseId, setEnterpriseId] = useState('');
  const [enterpriseDetails, setEnterpriseDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  // Keep using enterpriseDetails for licensing checks (source of truth)
  
  // User management state
  const [users, setUsers] = useState([]);
  const [licenseUsage, setLicenseUsage] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteAdmin, setInviteAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Report types state
// Report types state
const [reportTypes, setReportTypes] = useState([]);
const [filteredReportTypes, setFilteredReportTypes] = useState([]);
const [searchTerm, setSearchTerm] = useState('');
const [reportTypeLoading, setReportTypeLoading] = useState(false);

// Create/Edit report type state
const [isCreatingReportType, setIsCreatingReportType] = useState(false);
const [newReportType, setNewReportType] = useState({
  name: '',
  description: '',
  enterpriseExampleProportion: 0.75,
  customEnterpriseInstructions: ''
});
const [editingReportTypeId, setEditingReportTypeId] = useState(null);
const [editedReportType, setEditedReportType] = useState({});

// Examples state
const [expandedReportTypes, setExpandedReportTypes] = useState({});
const [reportTypeExamples, setReportTypeExamples] = useState({});
const [loadingExamplesFor, setLoadingExamplesFor] = useState(null);
const [examplesLoading, setExamplesLoading] = useState(false);

// Create/Edit example state
const [creatingExampleForType, setCreatingExampleForType] = useState(null);
const [showTranscriptionField, setShowTranscriptionField] = useState(false);
const [newExample, setNewExample] = useState({
  transcription: '',
  correctedContent: ''
});
const [editingExampleId, setEditingExampleId] = useState(null);
const [editedExample, setEditedExample] = useState({});

// Multiple creation and expand/collapse state
const [creatingExamples, setCreatingExamples] = useState(new Map());
const [expandedExamples, setExpandedExamples] = useState(new Set());

  // Enterprise editing state
  const [isEditingEnterprise, setIsEditingEnterprise] = useState(false);
  const [editedEnterprise, setEditedEnterprise] = useState({});
  const [editingLoading, setEditingLoading] = useState(false);
  const [showCustomSpecialty, setShowCustomSpecialty] = useState(false);
  const [customSpecialty, setCustomSpecialty] = useState('');
  // Delete enterprise state (only for none licensing)
  const [showDeleteEnterprise, setShowDeleteEnterprise] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  
  // Fullscreen editors for enterprise description
  const enterpriseDescriptionEditor = useFullScreenTextarea({
    value: editedEnterprise.description || '',
    onChange: (value) => setEditedEnterprise({...editedEnterprise, description: value}),
    editorOptions: {
      title: 'Description de l\'organisation',
      placeholder: 'Entrez la description de l\'organisation...',
    }
  });
  
  // Fullscreen editors for report type fields
  const newReportTypeDescriptionEditor = useFullScreenTextarea({
    value: newReportType.description || '',
    onChange: (value) => setNewReportType({...newReportType, description: value}),
    editorOptions: {
      title: 'Description du template',
      placeholder: 'Entrez la description du template...',
    }
  });
  
  const editedReportTypeDescriptionEditor = useFullScreenTextarea({
    value: editedReportType.description || '',
    onChange: (value) => setEditedReportType({...editedReportType, description: value}),
    editorOptions: {
      title: 'Description du template',
      placeholder: 'Entrez la description du template...',
    }
  });

  const newReportTypeInstructionsEditor = useFullScreenTextarea({
    value: newReportType.customEnterpriseInstructions || '',
    onChange: (value) => setNewReportType({...newReportType, customEnterpriseInstructions: value}),
    editorOptions: {
      title: 'Instructions personnalisées du template',
      placeholder: 'Entrez les instructions personnalisées pour ce template...',
    }
  });

  const editedReportTypeInstructionsEditor = useFullScreenTextarea({
    value: editedReportType.customEnterpriseInstructions || '',
    onChange: (value) => setEditedReportType({...editedReportType, customEnterpriseInstructions: value}),
    editorOptions: {
      title: 'Instructions personnalisées du template',
      placeholder: 'Entrez les instructions personnalisées pour ce template...',
    }
  });
  
  // Fullscreen editors for examples
  const newExampleTranscriptionEditor = useFullScreenTextarea({
    value: newExample.transcription || '',
    onChange: (value) => setNewExample({...newExample, transcription: value}),
    editorOptions: {
      title: 'Transcription',
      placeholder: 'Entrez le texte de transcription original (optionnel)...',
    }
  });
  
  const newExampleContentEditor = useFullScreenTextarea({
    value: newExample.correctedContent || '',
    onChange: (value) => setNewExample({...newExample, correctedContent: value}),
    editorOptions: {
      title: 'Contenu corrigé',
      placeholder: 'Entrez la version corrigée du texte...',
    }
  });
  
  const editedExampleTranscriptionEditor = useFullScreenTextarea({
    value: editedExample.transcription || '',
    onChange: (value) => setEditedExample({...editedExample, transcription: value}),
    editorOptions: {
      title: 'Transcription',
      placeholder: 'Entrez le texte de transcription original...',
    }
  });
  
  const editedExampleContentEditor = useFullScreenTextarea({
    value: editedExample.correctedContent || '',
    onChange: (value) => setEditedExample({...editedExample, correctedContent: value}),
    editorOptions: {
      title: 'Contenu corrigé',
      placeholder: 'Entrez la version corrigée du texte...',
    }
  });

  // Access logs state
  const [accessLogs, setAccessLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage] = useState(50);
  const [logFilters, setLogFilters] = useState({
    startDate: '',
    endDate: '',
    action: '',
    userId: ''
  });
  const [accessLogsLoading, setAccessLogsLoading] = useState(false);
  
  // Enterprise template management state
  const [enterpriseTemplates, setEnterpriseTemplates] = useState([]);
  const [enterpriseTemplateLoading, setEnterpriseTemplateLoading] = useState(false);
  const [enterpriseTemplateError, setEnterpriseTemplateError] = useState('');
  // Enterprise default template state
  const [enterpriseDefaultTemplate, setEnterpriseDefaultTemplate] = useState(null);
  const [enterpriseDefaultTemplateLoading, setEnterpriseDefaultTemplateLoading] = useState(false);
  const [enterpriseDefaultTemplateError, setEnterpriseDefaultTemplateError] = useState('');
  const enterpriseDefaultTemplateFileInputRef = React.useRef(null);
  // Template upload modal state
  const [pendingTemplateFile, setPendingTemplateFile] = useState(null);
  const [showReportTypeModal, setShowReportTypeModal] = useState(false);
  const [selectedReportTypeForUpload, setSelectedReportTypeForUpload] = useState('tous');
  const [exampleTemplates, setExampleTemplates] = useState([]);
  const [exampleTemplatesLoading, setExampleTemplatesLoading] = useState(false);
  const [selectedExampleId, setSelectedExampleId] = useState('');

  // Full screen editor state
  const [showInstructionEditor, setShowInstructionEditor] = useState(false);
  const [editorMode, setEditorMode] = useState('create'); // 'create' or 'edit'
  const [currentEditingReportTypeId, setCurrentEditingReportTypeId] = useState(null);

  const { showNotification } = useNotification();

  // Get current user profile and available report types

  const availableReportTypes = React.useMemo(() => {
    if (!profile?.reportTypeNames) return [];
    
    const reportTypeNames = profile.reportTypeNames;
    const allTypes = [];
    
    // Add default types
    if (reportTypeNames.data.default) {
      allTypes.push(...reportTypeNames.data.default.map(name => ({ name, category: 'default' })));
    }
    
    // Add favorite types
    if (reportTypeNames.data.favorites) {
      allTypes.push(...reportTypeNames.data.favorites.map(name => ({ name, category: 'favorites' })));
    }
    
    // Add personal types
    if (reportTypeNames.data.personal) {
      allTypes.push(...reportTypeNames.data.personal.map(name => ({ name, category: 'personal' })));
    }
    
    // Add enterprise types (for enterprise templates, we can use enterprise report types)
    Object.keys(reportTypeNames.data).forEach(key => {
      if (key.startsWith('enterprise_')) {
        allTypes.push(...reportTypeNames.data[key].map(name => ({ name, category: key })));
      }
    });
    
    // Remove duplicates
    const uniqueTypes = [];
    const seenNames = new Set();
    
    allTypes.forEach(type => {
      if (!seenNames.has(type.name)) {
        seenNames.add(type.name);
        uniqueTypes.push(type);
      }
    });
    
    return uniqueTypes;
  }, [profile?.reportTypeNames]);
  
  // Helper function to format report type names for display
  const formatReportTypeName = (typeName) => {
    if (typeof typeName === 'string') {
      return typeName.charAt(0).toUpperCase() + typeName.slice(1).replace(/_/g, ' ');
    }
    return typeName.name || typeName;
  };

  useEffect(() => {
    // Set loading immediately on mount
    setIsInitialLoading(true);
    loadEnterprises();
  }, []);

  
  const loadEnterprises = async () => {
    const minimumLoadingTime = 500;
    const startTime = Date.now();
    
    try {
      // Get enterprises from API - this should return enterprises with isAdmin info
      const allEnterprises = await enterpriseService.getMyEnterprises();
      log.debug('allEnterprises', allEnterprises);

      // Filter enterprises where the current user is admin
      // The API response should include isAdmin property for each enterprise
      const adminEnterprises = allEnterprises.filter(e => e.isAdmin === true);
          
      // Store the enterprises with their admin status
      const enterprisesWithAdminStatus = adminEnterprises.map(e => ({
        ...new Enterprise(e),
        isAdmin: e.isAdmin
      }));
      
      setEnterprises(enterprisesWithAdminStatus);

      // Select the first enterprise if available
      if (enterprisesWithAdminStatus.length > 0) {
        setEnterpriseId(enterprisesWithAdminStatus[0].id);
      }

      if (enterpriseId) {
        loadEnterpriseData(enterpriseId);
        loadReportTypes(enterpriseId);
      }

      // Ensure minimum loading time
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
    } catch (err) {
      log.error('Error loading enterprises', err);
      
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
    } finally {
     
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    if (!enterpriseId) return;
    if (activeTab === 'accessLogs' && !(enterpriseDetails && enterpriseDetails.licensingMode === 'none')) {
      loadAccessLogs(enterpriseId);
    }
  }, [activeTab, enterpriseId, enterpriseDetails]);

  useEffect(() => {
    if (enterpriseId) {
      loadEnterpriseData(enterpriseId);
      loadReportTypes(enterpriseId);
      if (activeTab === 'accessLogs' && !(enterpriseDetails && enterpriseDetails.licensingMode === 'none')) {
        loadAccessLogs(enterpriseId);
      }
    }
  }, [enterpriseId, activeTab]);

  // Ensure we don't stay on access logs when enterprise has no licensing
  useEffect(() => {
    if (enterpriseDetails && enterpriseDetails.licensingMode === 'none' && activeTab === 'accessLogs') {
      setActiveTab('general');
    }
  }, [enterpriseDetails, activeTab]);

  const loadEnterpriseData = async (id) => {
    setLoading(true);
    try {
      // Fetch users and details first
      const [u, details] = await Promise.all([
        enterpriseAdminService.getEnterpriseUsers(id),
        enterpriseAdminService.getEnterpriseDetails(id)
      ]);
      setUsers(u);
      setEnterpriseDetails(details);

      // Then, if licensing is enabled, fetch license usage
      if (details && details.licensingMode !== 'none') {
        const license = await enterpriseAdminService.getLicenseUsage(id);
        setLicenseUsage(license);
      } else {
        setLicenseUsage(null);
      }
    } catch (err) {
      log.error('Error loading enterprise data', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail) return;
    
    // Show appropriate confirmation message based on admin status
    let confirmMessage = `Cet utilisateur sera invité et rejoindra pourra rejoindre votre organisation. Vous pourrez le supprimer de l'organisation si nécessaire.\n\nVoulez-vous continuer ?`;
    
    if (inviteAdmin) {
      confirmMessage = `ATTENTION : Cet utilisateur sera invité en tant qu'administrateur.\n\nIl aura tous les accès comme vous et pourra :\n- Inviter et supprimer des utilisateurs\n- Gérer les templates Word\n- Accéder à toutes les fonctionnalités d'administration\n\nÊtes-vous sûr de vouloir continuer ?`;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await enterpriseAdminService.inviteUser(enterpriseId, inviteEmail, inviteAdmin);
      setInviteEmail('');
      setInviteAdmin(false);
      loadEnterpriseData(enterpriseId);
    } catch (err) {
      log.error('Error inviting user', err);
    }
  };

  const removeUser = async (id) => {
    if (!window.confirm('Retirer cet utilisateur ?')) return;
    try {
      await enterpriseAdminService.removeUser(enterpriseId, id);
      loadEnterpriseData(enterpriseId);
    } catch (err) {
      log.error('Error removing user', err);
    }
  };

  const toggleAdmin = async (u) => {
    try {
      await enterpriseAdminService.setUserAdmin(enterpriseId, u.userId, !u.isAdmin);
      loadEnterpriseData(enterpriseId);
    } catch (err) {
      log.error('Error updating admin status', err);
    }
  };

  // Report Types Management Functions
  const loadReportTypes = async (id) => {
    try {
      const types = await enterpriseAdminService.getEnterpriseReportTypes(id);
      setReportTypes(types);
      setFilteredReportTypes(types);
    } catch (err) {
      log.error('Error loading report types', err);
    }
  };
  
  const handleReportTypeSearch = (term) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredReportTypes(reportTypes);
    } else {
      const filtered = reportTypes.filter(type => 
        (type.name && type.name.toLowerCase().includes(term.toLowerCase())) ||
        (type.description && type.description.toLowerCase().includes(term.toLowerCase())) ||
        (type.customEnterpriseInstructions && type.customEnterpriseInstructions.toLowerCase().includes(term.toLowerCase()))
      );
      setFilteredReportTypes(filtered);
    }
  };
  
  // Create Report Type
  const startCreateReportType = () => {
    setIsCreatingReportType(true);
    setNewReportType({
      name: '',
      description: '',
      enterpriseExampleProportion: 0.75,
      customEnterpriseInstructions: ''
    });
  };
  
  const cancelCreateReportType = () => {
    setIsCreatingReportType(false);
    setNewReportType({
      name: '',
      description: '',
      enterpriseExampleProportion: 0.75,
      customEnterpriseInstructions: ''
    });
  };
  
  const saveCreateReportType = async () => {
    const trimmedName = newReportType.name.trim();
    
    // Validate name is not empty
    if (!trimmedName) {
      alert('Le nom est requis');
      return;
    }
    
    // Validate name length (1-50 characters)
    if (trimmedName.length > 50) {
      alert('Le nom doit contenir au maximum 50 caractères');
      return;
    }
    
    // Check maximum report types limit (10 per enterprise)
    if (reportTypes.length >= 10) {
      alert('Limite atteinte : maximum 10 types de rapport par organisation');
      return;
    }
    
    // Check for duplicate name
    const duplicateExists = reportTypes.some(rt => 
      rt.name.toLowerCase() === trimmedName.toLowerCase()
    );
    
    if (duplicateExists) {
      alert('Un template avec ce nom existe déjà');
      return;
    }
    
    try {
      setReportTypeLoading(true);
      await enterpriseAdminService.createEnterpriseReportType(enterpriseId, {
        name: newReportType.name,
        description: newReportType.description,
        enterpriseExampleProportion: newReportType.enterpriseExampleProportion,
        customEnterpriseInstructions: newReportType.customEnterpriseInstructions,
        // Set default values for required backend fields
        systemPrompt: '', // Can be added later if needed
        examplesCount: 15,
        model: 'gpt-4.1',
        temperature: 0.2,
        maxTokens: 4000,
        isJson: false
      });
      await loadReportTypes(enterpriseId);
      cancelCreateReportType();
    } catch (err) {
      log.error('Error creating report type:', err);
      alert('Erreur lors de la création du template');
    } finally {
      setReportTypeLoading(false);
    }
  };
  
  // Edit Report Type
  const startEditReportType = (reportType) => {
    setEditingReportTypeId(reportType.id);
    setEditedReportType({
      name: reportType.name,
      description: reportType.description || '',
      enterpriseExampleProportion: reportType.enterpriseExampleProportion || 0.75,
      customEnterpriseInstructions: reportType.customEnterpriseInstructions || '',
      isActive: reportType.isActive !== false
    });
  };
  
  const cancelEditReportType = () => {
    setEditingReportTypeId(null);
    setEditedReportType({});
  };
  
  const saveEditReportType = async (reportTypeId) => {
    if (!editedReportType.name.trim()) {
      alert('Le nom est requis');
      return;
    }
    
    try {
      setReportTypeLoading(true);
      await enterpriseAdminService.updateEnterpriseReportType(enterpriseId, reportTypeId, editedReportType);
      await loadReportTypes(enterpriseId);
      cancelEditReportType();
    } catch (err) {
      log.error('Error updating report type:', err);
      alert('Erreur lors de la mise à jour du template');
    } finally {
      setReportTypeLoading(false);
    }
  };
  
  // Delete Report Type
  const deleteReportType = async (reportTypeId, reportTypeName) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le type "${reportTypeName}" ?\n\nCette action supprimera également tous les exemples associés.`)) {
      try {
        setReportTypeLoading(true);
        await enterpriseAdminService.deleteEnterpriseReportType(enterpriseId, reportTypeId);
        // Remove examples from state
        setReportTypeExamples(prev => {
          const newExamples = {...prev};
          delete newExamples[reportTypeName];
          return newExamples;
        });
        await loadReportTypes(enterpriseId);
      } catch (err) {
        log.error('Error deleting report type:', err);
        alert('Erreur lors de la suppression du template');
      } finally {
        setReportTypeLoading(false);
      }
    }
  };
  
  // Toggle expanded state and load examples
  const toggleReportTypeExpanded = async (reportTypeId) => {
    const reportType = reportTypes.find(rt => rt.id === reportTypeId);
    if (!reportType) return;
  
    const isExpanded = expandedReportTypes[reportTypeId];
    
    if (!isExpanded) {
      // Load examples when expanding
      await loadExamplesForReportType(reportType.name);
    }
    
    setExpandedReportTypes(prev => ({
      ...prev,
      [reportTypeId]: !isExpanded
    }));
  };
  
  // Examples Management Functions
  
  const loadExamplesForReportType = async (reportTypeName) => {
    try {
      setLoadingExamplesFor(reportTypeName);
      const examples = await enterpriseAdminService.getEnterpriseExamples(enterpriseId, reportTypeName);
      setReportTypeExamples(prev => ({
        ...prev,
        [reportTypeName]: examples
      }));
    } catch (err) {
      log.error('Error loading examples:', err);
    } finally {
      setLoadingExamplesFor(null);
    }
  };
  
  // Create Example
  const startCreateExample = (reportTypeName) => {
    setCreatingExampleForType(reportTypeName);
    setShowTranscriptionField(false);
    setNewExample({
      transcription: '',
      correctedContent: ''
    });
  };
  
  const cancelCreateExample = () => {
    setCreatingExampleForType(null);
    setShowTranscriptionField(false);
    setNewExample({
      transcription: '',
      correctedContent: ''
    });
  };
  
  const saveCreateExample = async (reportTypeName) => {
    if (!newExample.correctedContent.trim()) {
      alert('Le contenu corrigé est requis');
      return;
    }
    
    // Generate temporary ID for tracking
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    
    try {
      const exampleData = {
        reportType: reportTypeName,
        correctedContent: newExample.correctedContent
      };
      
      // Only include transcription if it's provided
      if (newExample.transcription.trim()) {
        exampleData.transcription = newExample.transcription;
      }
      
      // Add to creating examples map
      setCreatingExamples(prev => {
        const newMap = new Map(prev);
        newMap.set(tempId, {
          reportType: reportTypeName,
          content: exampleData,
          status: 'creating'
        });
        return newMap;
      });
      
      // Clear form for next example
      setNewExample({
        transcription: '',
        correctedContent: ''
      });
      setShowTranscriptionField(false);
      
      // Create example in background
      await enterpriseAdminService.createEnterpriseExample(enterpriseId, exampleData);
      await loadExamplesForReportType(reportTypeName);
      
      // Remove from creating examples map
      setCreatingExamples(prev => {
        const newMap = new Map(prev);
        newMap.delete(tempId);
        return newMap;
      });
    } catch (err) {
      log.error('Error creating example:', err);
      
      // Update status to error
      setCreatingExamples(prev => {
        const newMap = new Map(prev);
        const example = newMap.get(tempId);
        if (example) {
          newMap.set(tempId, { ...example, status: 'error' });
        }
        return newMap;
      });
      
      // Remove error after 3 seconds
      setTimeout(() => {
        setCreatingExamples(prev => {
          const newMap = new Map(prev);
          newMap.delete(tempId);
          return newMap;
        });
      }, 3000);
    }
  };
  
  // Edit Example
  const startEditExample = (example) => {
    setEditingExampleId(example.id);
    setEditedExample({
      transcription: example.transcription,
      correctedContent: example.correctedContent
    });
  };
  
  const cancelEditExample = () => {
    setEditingExampleId(null);
    setEditedExample({});
  };
  
  const saveEditExample = async (exampleId) => {
    try {
      setExamplesLoading(true);
      await enterpriseAdminService.updateEnterpriseExample(enterpriseId, exampleId, editedExample);
      
      // Find which report type this example belongs to and reload
      for (const [reportTypeName, examples] of Object.entries(reportTypeExamples)) {
        if (examples.some(ex => ex.id === exampleId)) {
          await loadExamplesForReportType(reportTypeName);
          break;
        }
      }
      
      cancelEditExample();
    } catch (err) {
      log.error('Error updating example:', err);
      alert('Erreur lors de la mise à jour de l\'exemple');
    } finally {
      setExamplesLoading(false);
    }
  };
  
  // Delete Example
  const deleteExample = async (exampleId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet exemple ?')) {
      try {
        setExamplesLoading(true);
        await enterpriseAdminService.deleteEnterpriseExample(enterpriseId, exampleId);
        
        // Find which report type this example belongs to and reload
        for (const [reportTypeName, examples] of Object.entries(reportTypeExamples)) {
          if (examples.some(ex => ex.id === exampleId)) {
            await loadExamplesForReportType(reportTypeName);
            break;
          }
        }
      } catch (err) {
        log.error('Error deleting example:', err);
        alert('Erreur lors de la suppression de l\'exemple');
      } finally {
        setExamplesLoading(false);
      }
    }
  };
  


  // Access Logs Functions
  const loadAccessLogs = async (enterpriseId, page = 1) => {
    setAccessLogsLoading(true);
    try {
      const queryParams = {
        enterpriseId,
        page,
        limit: logsPerPage,
        ...logFilters
      };

      const { logs, total } = await enterpriseAdminService.getEnterpriseAccessLogs(queryParams);
      setAccessLogs(logs);
      setTotalLogs(total);
      setCurrentPage(page);
    } catch (err) {
      log.error('Error loading access logs:', err);
      alert('Erreur lors du chargement des journaux d\'accès');
    } finally {
      setAccessLogsLoading(false);
    }
  };

  const handleLogFilterChange = (filterName, value) => {
    setLogFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const applyLogFilters = () => {
    setCurrentPage(1);
    loadAccessLogs(enterpriseId, 1);
  };

  // Enterprise Template Management Functions
  const loadEnterpriseTemplates = useCallback(async () => {
    setEnterpriseTemplateLoading(true);
    setEnterpriseTemplateError('');
    try {
      const enterpriseTemplates = await wordService.getEnterpriseTemplates();
      

      
      // For enterprise templates, we want to show all templates returned by the API
      // The backend should already filter to only return enterprise templates
      setEnterpriseTemplates(enterpriseTemplates || []);
    } catch (error) {
      log.error('Error loading enterprise templates:', error);
      setEnterpriseTemplateError('Impossible de charger les modèles');
    } finally {
      setEnterpriseTemplateLoading(false);
    }
  }, [showNotification]);

  const uploadEnterpriseTemplate = useCallback(async (file, reportTypeId = null) => {
    setEnterpriseTemplateLoading(true);
    setEnterpriseTemplateError('');
    try {
      // Find report type details if ID provided
      let reportTypeName = null;
      if (reportTypeId && reportTypeId !== 'tous') {
        const reportType = reportTypes.find(t => String(t.id) === String(reportTypeId));
        if (reportType) {
          reportTypeName = reportType.name;
        }
      }

      // Upload with report type included in FormData (single API call)
      await wordService.uploadEnterpriseTemplate(file, reportTypeId, reportTypeName);

      showNotification('Modèle téléchargé avec succès!', 'success');

      // Refresh templates list
      await loadEnterpriseTemplates();
    } catch (error) {
      log.error('Error uploading enterprise template:', error);
      setEnterpriseTemplateError('Erreur lors du téléchargement du modèle');
      showNotification('Erreur lors du téléchargement du modèle', 'error');
      throw error;
    } finally {
      setEnterpriseTemplateLoading(false);
    }
  }, [loadEnterpriseTemplates, reportTypes, showNotification]);

  // Handle when user selects a template file - opens modal to choose report type
  const handleTemplateFileSelected = useCallback((file) => {
    setPendingTemplateFile(file);
    // Set default to first available report type's ID (excluding those that already have templates)
    const usedReportTypeIds = enterpriseTemplates.map(t => t.reportTypeId).filter(Boolean);
    const availableTypes = reportTypes.filter(t => !usedReportTypeIds.includes(t.id));
    const firstAvailableId = availableTypes.length > 0 ? availableTypes[0].id : (reportTypes.length > 0 ? reportTypes[0].id : '');
    setSelectedReportTypeForUpload(firstAvailableId);
    setShowReportTypeModal(true);
  }, [reportTypes, enterpriseTemplates]);

  // Confirm upload with selected report type
  const confirmTemplateUpload = useCallback(async () => {
    if (!pendingTemplateFile) return;

    try {
      await uploadEnterpriseTemplate(pendingTemplateFile, selectedReportTypeForUpload);
      // Close modal and reset state on success
      setShowReportTypeModal(false);
      setPendingTemplateFile(null);
      setSelectedReportTypeForUpload('tous');
    } catch (error) {
      // Error already handled in uploadEnterpriseTemplate
      // Keep modal open so user can retry or cancel
    }
  }, [pendingTemplateFile, selectedReportTypeForUpload, uploadEnterpriseTemplate]);

  // Cancel template upload
  const cancelTemplateUpload = useCallback(() => {
    setShowReportTypeModal(false);
    setPendingTemplateFile(null);
    setSelectedReportTypeForUpload('tous');
  }, []);

  const deleteEnterpriseTemplate = useCallback(async (templateId) => {
    setEnterpriseTemplateLoading(true);
    setEnterpriseTemplateError('');
    try {
      await wordService.deleteEnterpriseTemplate(templateId);
      
      // Remove the deleted template from the list
      const updatedTemplates = enterpriseTemplates.filter(t => t.id !== templateId);
      setEnterpriseTemplates(updatedTemplates);
      showNotification('Modèle supprimé avec succès!', 'success');
    } catch (error) {
      log.error('Error deleting enterprise template:', error);
      setEnterpriseTemplateError('Erreur lors de la suppression du modèle');
      showNotification('Erreur lors de la suppression du modèle', 'error');
      throw error;
    } finally {
      setEnterpriseTemplateLoading(false);
    }
  }, [enterpriseTemplates, showNotification]);

  const setEnterpriseTemplateReportType = useCallback(async (template, reportType, enterpriseReportTypeId) => {
    setEnterpriseTemplateLoading(true);
    setEnterpriseTemplateError('');
    try {
      // Skip system default templates (they have a specific ID pattern)
      if (template.id === '00000000-0000-0000-0000-000000000001') {
        showNotification('Le modèle par défaut du système ne peut pas être modifié', 'warning');
        return;
      }
      
      await wordService.updateEnterpriseTemplate(template.id, { reportType, enterpriseReportTypeId });
      
      setEnterpriseTemplates(prev => prev.map(t => 
        t.id === template.id ? { ...t, reportType, reportTypeId: enterpriseReportTypeId } : t
      ));
      
      showNotification('Template mis à jour avec succès!', 'success');
    } catch (error) {
      log.error('Error updating enterprise template report type:', error);
      setEnterpriseTemplateError('Erreur lors de la mise à jour du template');
      showNotification('Erreur lors de la mise à jour du template', 'error');
      throw error;
    } finally {
      setEnterpriseTemplateLoading(false);
    }
  }, [showNotification]);

  const downloadEnterpriseTemplate = useCallback(async (template) => {
    setEnterpriseTemplateLoading(true);
    setEnterpriseTemplateError('');
    try {
      const blob = await wordService.downloadEnterpriseTemplate(template.id);
      
      if (blob && blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${template.name || 'template'}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showNotification('Modèle téléchargé avec succès!', 'success');
      } else {
        throw new Error('Aucun fichier retourné');
      }
    } catch (error) {
      log.error('Error downloading enterprise template:', error);
      setEnterpriseTemplateError('Erreur lors du téléchargement du modèle');
      showNotification('Erreur lors du téléchargement du modèle', 'error');
      throw error;
    } finally {
      setEnterpriseTemplateLoading(false);
    }
  }, [showNotification]);

  // Enterprise default template management functions
  const loadEnterpriseDefaultTemplate = useCallback(async () => {
    if (!enterpriseId) return;

    setEnterpriseDefaultTemplateLoading(true);
    setEnterpriseDefaultTemplateError('');
    try {
      const template = await wordService.getEnterpriseDefaultTemplate(enterpriseId);
      setEnterpriseDefaultTemplate(template); // Can be null
    } catch (error) {
      log.error('Error loading enterprise default template:', error);
      setEnterpriseDefaultTemplateError('Impossible de charger le template par défaut');
      setEnterpriseDefaultTemplate(null);
    } finally {
      setEnterpriseDefaultTemplateLoading(false);
    }
  }, [enterpriseId]);

  const uploadEnterpriseDefaultTemplate = useCallback(async (file) => {
    if (!file || !enterpriseId) return;

    setEnterpriseDefaultTemplateError('');

    // Validate file type
    if (!file.name.endsWith('.docx')) {
      setEnterpriseDefaultTemplateError('Le fichier doit être un document .docx');
      showNotification('Le fichier doit être un document .docx', 'error');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setEnterpriseDefaultTemplateError('Taille maximale 10 Mo.');
      showNotification('Le fichier ne doit pas dépasser 10 Mo', 'error');
      return;
    }

    setEnterpriseDefaultTemplateLoading(true);
    try {
      const template = await wordService.uploadEnterpriseDefaultTemplate(file, enterpriseId);
      setEnterpriseDefaultTemplate(template);
      showNotification('Template par défaut de l\'entreprise téléchargé avec succès!', 'success');
    } catch (error) {
      log.error('Error uploading enterprise default template:', error);
      setEnterpriseDefaultTemplateError('Échec du téléchargement du template par défaut.');
      showNotification('Erreur lors du téléchargement du template', 'error');
    } finally {
      setEnterpriseDefaultTemplateLoading(false);
    }
  }, [enterpriseId, showNotification]);

  const deleteEnterpriseDefaultTemplate = useCallback(async () => {
    if (!enterpriseId) return;

    if (!window.confirm('Supprimer le template par défaut de l\'entreprise ? Cette action est irréversible.')) {
      return;
    }

    setEnterpriseDefaultTemplateLoading(true);
    setEnterpriseDefaultTemplateError('');
    try {
      await wordService.deleteEnterpriseDefaultTemplate(enterpriseId);
      setEnterpriseDefaultTemplate(null);
      showNotification('Template par défaut de l\'entreprise supprimé avec succès!', 'success');
    } catch (error) {
      log.error('Error deleting enterprise default template:', error);
      setEnterpriseDefaultTemplateError('Échec de la suppression du template par défaut.');
      showNotification('Erreur lors de la suppression du template', 'error');
    } finally {
      setEnterpriseDefaultTemplateLoading(false);
    }
  }, [enterpriseId, showNotification]);

  const openDeleteEnterpriseModal = () => {
    setDeleteError('');
    setDeleteConfirmName('');
    setShowDeleteEnterprise(true);
  };

  const handleConfirmDeleteEnterprise = async () => {
    if (!enterpriseDetails || !enterpriseId) return;
    if ((deleteConfirmName || '').trim() !== (enterpriseDetails.name || '').trim()) {
      setDeleteError('Le nom ne correspond pas.');
      return;
    }
    try {
      setDeleteLoading(true);
      await adminService.deleteEnterprise(enterpriseId);
      setShowDeleteEnterprise(false);
      setDeleteConfirmName('');
      setDeleteError('');
      showNotification('Organisation supprimée', 'success');
      await loadEnterprises();
    } catch (err) {
      log.error('Error deleting enterprise', err);
      setDeleteError("Erreur lors de la suppression. Réessayez.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const loadExampleTemplates = useCallback(async () => {
    setExampleTemplatesLoading(true);
    try {
      const examples = await wordService.getExampleTemplates();
      setExampleTemplates(examples || []);
    } catch (error) {
      log.error('Error loading example templates:', error);
      setExampleTemplates([]);
    } finally {
      setExampleTemplatesLoading(false);
    }
  }, []);

  const downloadExampleTemplate = useCallback(async () => {
    if (!selectedExampleId) {
      showNotification('Veuillez sélectionner un modèle d\'exemple', 'warning');
      return;
    }
    
    const example = exampleTemplates.find(ex => ex.id === selectedExampleId);
    if (!example) {
      showNotification('Modèle d\'exemple non trouvé', 'error');
      return;
    }
    
    setEnterpriseTemplateLoading(true);
    setEnterpriseTemplateError('');
    try {
      const blob = await wordService.downloadExampleTemplate(example.id);
      
      if (blob && blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = example.filename || `exemple_${example.reportType}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showNotification(`Exemple ${formatReportTypeName(example.reportType)} téléchargé avec succès!`, 'success');
      } else {
        throw new Error('Aucun fichier retourné');
      }
    } catch (error) {
      log.error('Error downloading example template:', error);
      setEnterpriseTemplateError('Erreur lors du téléchargement de l\'exemple');
      showNotification('Erreur lors du téléchargement de l\'exemple', 'error');
    } finally {
      setEnterpriseTemplateLoading(false);
    }
  }, [selectedExampleId, exampleTemplates, showNotification]);

  // Load enterprise templates when templates tab is active
  useEffect(() => {
    if (enterpriseId && activeTab === 'templates') {
      loadEnterpriseTemplates();
      loadExampleTemplates();
      loadEnterpriseDefaultTemplate();
    }
  }, [activeTab, enterpriseId, loadEnterpriseTemplates, loadExampleTemplates, loadEnterpriseDefaultTemplate]);

  const formatActionName = (action) => {
    const actionMap = {
      // User actions
      'login': 'Connexion',
      'logout': 'Déconnexion',
      
      // Enterprise management
      'create_enterprise': 'Création d\'organisation',
      'update_enterprise_details': 'Modification des informations',
      'set_active_enterprise': 'Changement d\'organisation active',
      'leave_enterprise': 'Départ de l\'organisation',
      
      // User management
      'invite_user': 'Invitation utilisateur',
      'remove_user': 'Retrait utilisateur',
      'update_admin': 'Modification statut admin',
      
      // Report management
      'create_report': 'Création de rapport',
      'update_report': 'Modification de rapport',
      'delete_report': 'Suppression de rapport',
      'view_report': 'Consultation de rapport',
      'export_report': 'Export de rapport',
      
      // Enterprise report types
      'create_enterprise_report_type': 'Création template',
      'update_enterprise_report_type': 'Modification template',
      'delete_enterprise_report_type': 'Suppression template',
      
      // Enterprise examples
      'create_enterprise_example': 'Ajout d\'exemple',
      'update_enterprise_example': 'Modification d\'exemple',
      'delete_enterprise_example': 'Suppression d\'exemple'
    };
    return actionMap[action] || action;
  };

  // Handle instruction editor save
  const handleInstructionSave = async (value) => {
    if (editorMode === 'create') {
      setNewReportType({ ...newReportType, customEnterpriseInstructions: value });
    } else if (editorMode === 'edit') {
      setEditedReportType({ ...editedReportType, customEnterpriseInstructions: value });
    }
    setShowInstructionEditor(false);
  };

  const formatMetadata = (metadata, action) => {
    if (!metadata || Object.keys(metadata).length === 0) {
      return '-';
    }

    // Parse metadata based on action type
    switch (action) {
      // Enterprise management
      case 'create_enterprise':
        return (
          <div className="enterprise-admin-metadata-parsed">
            {metadata.newEnterpriseId && <div><strong>ID Organisation:</strong> {metadata.newEnterpriseId}</div>}
            {metadata.name && <div><strong>Nom:</strong> {metadata.name}</div>}
            {metadata.shortName && <div><strong>Nom court:</strong> {metadata.shortName}</div>}
            {metadata.specialty && <div><strong>Spécialité:</strong> {metadata.specialty}</div>}
          </div>
        );
      
      case 'update_enterprise_details':
        return (
          <div className="enterprise-admin-metadata-parsed">
            {metadata.updatedFields && metadata.updatedFields.length > 0 && (
              <div><strong>Champs modifiés:</strong> {metadata.updatedFields.join(', ')}</div>
            )}
          </div>
        );
      
      case 'set_active_enterprise':
      case 'leave_enterprise':
        return (
          <div className="enterprise-admin-metadata-parsed">
            {metadata.previousEnterpriseId && <div><strong>Organisation précédente:</strong> {metadata.previousEnterpriseId}</div>}
            {metadata.newEnterpriseId && <div><strong>Nouvelle organisation:</strong> {metadata.newEnterpriseId}</div>}
          </div>
        );
      
      // User management
      case 'invite_user':
        return (
          <div className="enterprise-admin-metadata-parsed">
            {metadata.invitedEmail && <div><strong>Email invité:</strong> {metadata.invitedEmail}</div>}
            {metadata.isAdmin !== undefined && <div><strong>Admin:</strong> {metadata.isAdmin ? 'Oui' : 'Non'}</div>}
            {metadata.userExisted !== undefined && <div><strong>Utilisateur existant:</strong> {metadata.userExisted ? 'Oui' : 'Non'}</div>}
          </div>
        );
      
      case 'remove_user':
        return (
          <div className="enterprise-admin-metadata-parsed">
            {metadata.removedUserId && <div><strong>ID Utilisateur retiré:</strong> {metadata.removedUserId}</div>}
          </div>
        );
      
      // Report management
      case 'create_report':
      case 'update_report':
      case 'delete_report':
      case 'view_report':
      case 'export_report':
        return (
          <div className="enterprise-admin-metadata-parsed">
            {metadata.reportId && <div><strong>Rapport ID:</strong> {metadata.reportId}</div>}
            {metadata.reportType && <div><strong>Type:</strong> {metadata.reportType}</div>}
            {metadata.title && <div><strong>Titre:</strong> {metadata.title}</div>}
            {metadata.format && <div><strong>Format:</strong> {metadata.format}</div>}
          </div>
        );
      
      // Enterprise report types
      case 'create_enterprise_report_type':
        return (
          <div className="enterprise-admin-metadata-parsed">
            {metadata.reportTypeId && <div><strong>Type ID:</strong> {metadata.reportTypeId}</div>}
            {metadata.name && <div><strong>Nom:</strong> {metadata.name}</div>}
          </div>
        );
      
      case 'update_enterprise_report_type':
        return (
          <div className="enterprise-admin-metadata-parsed">
            {metadata.reportTypeId && <div><strong>Type ID:</strong> {metadata.reportTypeId}</div>}
            {metadata.updatedFields && metadata.updatedFields.length > 0 && (
              <div><strong>Champs modifiés:</strong> {metadata.updatedFields.join(', ')}</div>
            )}
          </div>
        );
      
      case 'delete_enterprise_report_type':
        return (
          <div className="enterprise-admin-metadata-parsed">
            {metadata.reportTypeId && <div><strong>Type ID:</strong> {metadata.reportTypeId}</div>}
          </div>
        );
      
      // Enterprise examples
      case 'create_enterprise_example':
        return (
          <div className="enterprise-admin-metadata-parsed">
            {metadata.exampleId && <div><strong>Exemple ID:</strong> {metadata.exampleId}</div>}
          </div>
        );
      
      case 'update_enterprise_example':
        return (
          <div className="enterprise-admin-metadata-parsed">
            {metadata.exampleId && <div><strong>Exemple ID:</strong> {metadata.exampleId}</div>}
            {metadata.updatedFields && metadata.updatedFields.length > 0 && (
              <div><strong>Champs modifiés:</strong> {metadata.updatedFields.join(', ')}</div>
            )}
          </div>
        );
      
      case 'delete_enterprise_example':
        return (
          <div className="enterprise-admin-metadata-parsed">
            {metadata.exampleId && <div><strong>Exemple ID:</strong> {metadata.exampleId}</div>}
          </div>
        );

      case 'update_admin':
        return (
          <div className="enterprise-admin-metadata-parsed">
            {metadata.targetUserId && <div><strong>Utilisateur ID:</strong> {metadata.targetUserId}</div>}
            {metadata.targetEmail && <div><strong>Email:</strong> {metadata.targetEmail}</div>}
            {metadata.newAdminStatus !== undefined && <div><strong>Nouveau statut:</strong> {metadata.newAdminStatus ? 'Admin' : 'Utilisateur'}</div>}
          </div>
        );
      
      default:
        // For unknown actions, show key-value pairs
        return (
          <div className="enterprise-admin-metadata-parsed">
            {Object.entries(metadata).map(([key, value]) => (
              <div key={key}>
                <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
              </div>
            ))}
          </div>
        );
    }
  };

  // Enterprise editing functions
  const startEditEnterprise = () => {
    setIsEditingEnterprise(true);
    const currentSpecialty = enterpriseDetails.specialty || '';
    
    // Check if current specialty is in the predefined list
    const isCustom = currentSpecialty && !SPECIALTIES.includes(currentSpecialty);
    
    setEditedEnterprise({
      name: enterpriseDetails.name,
      shortName: enterpriseDetails.shortName || '',
      description: enterpriseDetails.description || '',
      specialty: currentSpecialty
    });
    
    if (isCustom) {
      setShowCustomSpecialty(true);
      setCustomSpecialty(currentSpecialty);
    } else {
      setShowCustomSpecialty(false);
      setCustomSpecialty('');
    }
  };

  const cancelEditEnterprise = () => {
    setIsEditingEnterprise(false);
    setEditedEnterprise({});
    setShowCustomSpecialty(false);
    setCustomSpecialty('');
  };
  
  const handleSpecializationChange = (e) => {
    const value = e.target.value;
    
    if (value === 'other') {
      setShowCustomSpecialty(true);
      setEditedEnterprise({...editedEnterprise, specialty: ''});
    } else {
      setShowCustomSpecialty(false);
      setEditedEnterprise({...editedEnterprise, specialty: value});
      setCustomSpecialty('');
    }
  };

  const handleCustomSpecialtyChange = (e) => {
    const value = e.target.value;
    setCustomSpecialty(value);
    setEditedEnterprise({...editedEnterprise, specialty: value});
  };

  const handleCustomSpecialtyBlur = () => {
    // Already updating in onChange, so nothing special needed here
  };

  const saveEnterpriseDetails = async () => {
    // Build warning message
    
    let warningMessage = `Attention : Ces modifications seront visibles par tous les utilisateurs de l'organisation.`;
    
    // Check if specialty is being changed
    if (enterpriseDetails.specialty !== editedEnterprise.specialty) {
      warningMessage += `\n\nLa modification de la spécialité changera le template par défaut pour tous les utilisateurs.`;
    }
    
    warningMessage += `\n\nVoulez-vous continuer ?`;
    
    if (!window.confirm(warningMessage)) {
      return;
    }
    
    try {
      setEditingLoading(true);
      const updated = await enterpriseAdminService.updateEnterpriseDetails(enterpriseId, editedEnterprise);
      setEnterpriseDetails(updated);
      setIsEditingEnterprise(false);
      setEditedEnterprise({});
      setShowCustomSpecialty(false);
      setCustomSpecialty('');
    } catch (err) {
      log.error('Error updating enterprise details:', err);
      alert('Erreur lors de la mise à jour des détails de l\'organisation');
    } finally {
      setEditingLoading(false);
    }
  };

  // Early return with skeleton on initial page load only (not on tab switch)
  if (isInitialLoading) {
    return <SkeletonManager active={true} fullWidth={true} />;
  }

  if (enterprises.length === 0) {
      return <div className="enterprise-admin-container"><h2>Aucune organisation à administrer</h2></div>;
  }

  return (
    <div className="enterprise-admin-container">
      <BackButton />
      <div className="enterprise-admin-wrapper">
        <div className="enterprise-admin-header">      
          <div className="enterprise-admin-selector">
            <label>Organisation :</label>
            <select 
              value={enterpriseId} 
              onChange={e => {
                const newId = e.target.value;
                setEnterpriseId(newId);
                // Clear previous data while loading new
                setEnterpriseDetails(null);
                setUsers([]);
                setLicenseUsage(null);
              }}
            >
              {enterprises.map(e => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="enterprise-admin-tabs">
          <button 
            className={activeTab === 'general' ? 'active' : ''}
            onClick={() => setActiveTab('general')}
          >
            Général
          </button>
          <button 
            className={activeTab === 'users' ? 'active' : ''}
            onClick={() => setActiveTab('users')}
          >
            Utilisateurs
          </button>
          <button 
            className={activeTab === 'reportTypes' ? 'active' : ''}
            onClick={() => setActiveTab('reportTypes')}
          >
            Modèles
          </button>
          <button 
            className={activeTab === 'templates' ? 'active' : ''}
            onClick={() => setActiveTab('templates')}
          >
            Templates Word
          </button>
          {!(enterpriseDetails && enterpriseDetails.licensingMode === 'none') && (
            <button 
              className={activeTab === 'accessLogs' ? 'active' : ''}
              onClick={() => setActiveTab('accessLogs')}
            >
              Journaux d'accès
            </button>
          )}
        </div>

        {/* General Tab */}
        {activeTab === 'general' && (
          <GeneralTab
            enterpriseDetails={enterpriseDetails}
            editedEnterprise={editedEnterprise}
            isEditingEnterprise={isEditingEnterprise}
            startEditEnterprise={startEditEnterprise}
            cancelEditEnterprise={cancelEditEnterprise}
            handleSpecializationChange={handleSpecializationChange}
            handleCustomSpecialtyChange={handleCustomSpecialtyChange}
            handleCustomSpecialtyBlur={handleCustomSpecialtyBlur}
            saveEnterpriseDetails={saveEnterpriseDetails}
            editingLoading={editingLoading}
            licenseUsage={licenseUsage}
            setEditedEnterprise={setEditedEnterprise}
            showCustomSpecialty={showCustomSpecialty}
            customSpecialty={customSpecialty}
            enterpriseDescriptionEditor={enterpriseDescriptionEditor}
            onDeleteEnterprise={openDeleteEnterpriseModal}
          />
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <UsersTab
            users={users}
            loading={loading}
            handleInvite={handleInvite}
            inviteEmail={inviteEmail}
            inviteAdmin={inviteAdmin}
            setInviteEmail={setInviteEmail}
            setInviteAdmin={setInviteAdmin}
            removeUser={removeUser}
            toggleAdmin={toggleAdmin}
          />
        )}

        {/* Report Types Tab */}
        {activeTab === 'reportTypes' && (
          <ReportTypesTab enterpriseId={enterpriseId} enterpriseShortName={enterpriseDetails.shortName} />
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <TemplatesTab
            enterpriseId={enterpriseId}
            enterpriseTemplates={enterpriseTemplates}
            enterpriseTemplateLoading={enterpriseTemplateLoading}
            enterpriseTemplateError={enterpriseTemplateError}
            uploadEnterpriseTemplate={uploadEnterpriseTemplate}
            handleTemplateFileSelected={handleTemplateFileSelected}
            deleteEnterpriseTemplate={deleteEnterpriseTemplate}
            setEnterpriseTemplateReportType={setEnterpriseTemplateReportType}
            downloadEnterpriseTemplate={downloadEnterpriseTemplate}
            showReportTypeModal={showReportTypeModal}
            selectedReportTypeForUpload={selectedReportTypeForUpload}
            setSelectedReportTypeForUpload={setSelectedReportTypeForUpload}
            confirmTemplateUpload={confirmTemplateUpload}
            cancelTemplateUpload={cancelTemplateUpload}
            reportTypes={reportTypes}
            enterpriseDefaultTemplate={enterpriseDefaultTemplate}
            enterpriseDefaultTemplateLoading={enterpriseDefaultTemplateLoading}
            enterpriseDefaultTemplateError={enterpriseDefaultTemplateError}
            enterpriseDefaultTemplateFileInputRef={enterpriseDefaultTemplateFileInputRef}
            uploadEnterpriseDefaultTemplate={uploadEnterpriseDefaultTemplate}
            deleteEnterpriseDefaultTemplate={deleteEnterpriseDefaultTemplate}
            exampleTemplates={exampleTemplates}
            exampleTemplatesLoading={exampleTemplatesLoading}
            selectedExampleId={selectedExampleId}
            setSelectedExampleId={setSelectedExampleId}
            downloadExampleTemplate={downloadExampleTemplate}
            currentProfile={profile}
            availableReportTypes={reportTypes}
            formatReportTypeName={formatReportTypeName}
          />
        )}

        {/* Danger zone button moved into GeneralTab header */}

        {/* Access Logs Tab */}
        {activeTab === 'accessLogs' && !(enterpriseDetails && enterpriseDetails.licensingMode === 'none') && (
          <AccessLogsTab
            accessLogs={accessLogs}
            totalLogs={totalLogs}
            currentPage={currentPage}
            logsPerPage={logsPerPage}
            logFilters={logFilters}
            handleLogFilterChange={handleLogFilterChange}
            applyLogFilters={applyLogFilters}
            accessLogsLoading={accessLogsLoading}
            users={users}
            formatActionName={formatActionName}
            formatMetadata={formatMetadata}
          />
        )}
      </div>
      
      {/* Delete Enterprise Modal */}
      {showDeleteEnterprise && enterpriseDetails && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowDeleteEnterprise(false); }}>
          <div className="modal" style={{ maxWidth: 560 }} onMouseDown={(e) => e.stopPropagation()}>
            <h3>Supprimer l'organisation</h3>
            <p>
              Cette action est définitive et supprimera l'organisation « {enterpriseDetails.name} » et toutes ses données associées.
            </p>
            <p>Pour confirmer, tapez le nom exact de l'organisation :</p>
            <input
              type="text"
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              placeholder={enterpriseDetails.name}
              style={{ width: '100%' }}
            />
            {deleteError && (
              <div style={{ color: 'var(--color-danger)', marginTop: 8 }}>{deleteError}</div>
            )}
            <div className="modal-actions">
              <button onClick={() => setShowDeleteEnterprise(false)} disabled={deleteLoading}>Annuler</button>
              <button onClick={handleConfirmDeleteEnterprise} disabled={deleteLoading} style={{ background: '#c62828', color: '#fff' }}>
                {deleteLoading ? 'Suppression…' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Text Editor */}
      <FullScreenTextEditor
        isOpen={showInstructionEditor}
        onClose={() => {
          setShowInstructionEditor(false);
          setCurrentEditingReportTypeId(null);
        }}
        onSave={handleInstructionSave}
        initialValue={
          editorMode === 'create' 
            ? newReportType.customEnterpriseInstructions 
            : editedReportType.customEnterpriseInstructions || ''
        }
        title={
          editorMode === 'create'
            ? `Instructions pour ${newReportType.name || 'nouveau modèle'}`
            : `Instructions pour ${editedReportType.name || 'modèle'}`
        }
        placeholder="Entrez les instructions spécifiques à l'organisation pour ce modèle. Ces instructions seront appliquées à tous les utilisateurs de l'organisation lors de la génération de ce modèle."
        loading={false}
        saveButtonText="Appliquer les instructions"
      />
      
      {/* Full Screen Text Editors for Capacitor */}
      {isCapacitor && (
        <>
          {/* Enterprise Description */}
          <FullScreenTextEditor {...enterpriseDescriptionEditor.editorProps} />
          
          {/* Report Type Descriptions */}
          <FullScreenTextEditor {...newReportTypeDescriptionEditor.editorProps} />
          <FullScreenTextEditor {...editedReportTypeDescriptionEditor.editorProps} />
          
          {/* Example Fields */}
          <FullScreenTextEditor {...newExampleTranscriptionEditor.editorProps} />
          <FullScreenTextEditor {...newExampleContentEditor.editorProps} />
          <FullScreenTextEditor {...editedExampleTranscriptionEditor.editorProps} />
          <FullScreenTextEditor {...editedExampleContentEditor.editorProps} />
        </>
      )}
    </div>
  );
}

export default EnterpriseAdmin;